import type { ManifestEntry } from './registry';

export interface PreparedSession {
  roomName: string;
  token: string;
  wsUrl: string;
  participantIdentity: string;
  agent: { name: string };
  widget: {
    primaryColor: string;
    position: string;
    greeting: string | null;
    /** Present only when the API has suppressed this session's automatic greeting. */
    startup?: { greeting: string };
    defaults: {
      avatarEnabled: boolean;
      avatarVisible: boolean;
      avatarAvailable: boolean;
      avatarThumbnailUrl: string | null;
      /** CSS selector for the host-page element scenes render into. null
       *  (the common case) disables scenes for this site. Server config, not
       *  a `data-config` key — see `Website.sceneContainer`. */
      sceneContainer?: string | null;
      /** Per-website `Website.defaultMicMuted` / `defaultVolumeMuted`, set from
       * the admin panel's Widget settings tab. Both session routes have always
       * sent these; embed.js just never read them, so ticking the boxes did
       * nothing on a WIDGET site while hosted sites honoured them. */
      micMuted?: boolean;
      volumeMuted?: boolean;
    };
  };
}

export interface SessionClientOpts {
  baseUrl: string;
  apiKey: string;
}

export interface SessionClient {
  prepare(manifest: ManifestEntry[]): Promise<PreparedSession>;
  activate(roomName: string): Promise<void>;
  unified(manifest: ManifestEntry[], options?: { deferGreeting: boolean }): Promise<PreparedSession>;
}

/** Thrown for non-2xx session API responses; `status` lets callers branch on it. */
export class SessionApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'SessionApiError';
    this.status = status;
    this.code = code;
  }
}

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'omit',
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    let code: string | undefined;
    try {
      const e = await res.json();
      if (e?.error) msg = e.error;
      if (typeof e?.code === 'string') code = e.code;
    } catch {
      /* ignore */
    }
    throw new SessionApiError(msg, res.status, code);
  }
  return res.json() as Promise<T>;
}

export function createSessionClient(opts: SessionClientOpts): SessionClient {
  const { baseUrl, apiKey } = opts;
  return {
    async prepare(manifest) {
      return postJson<PreparedSession>(`${baseUrl}/api/widget/session/prepare`, {
        apiKey,
        siteFunctionManifest: manifest.length ? manifest : undefined,
      });
    },
    async activate(roomName) {
      await postJson<{ success: boolean }>(`${baseUrl}/api/widget/session/activate`, {
        apiKey,
        roomName,
      });
    },
    async unified(manifest, options) {
      return postJson<PreparedSession>(`${baseUrl}/api/widget/session`, {
        apiKey,
        ...(options?.deferGreeting ? { deferGreeting: true } : {}),
        siteFunctionManifest: manifest.length ? manifest : undefined,
      });
    },
  };
}
